import { useCallback, useRef } from "react";

import { extractDocumentText as extractDocumentTextApi } from "@/network/network";

import { ExtractionRequestBody } from "./useDocumentExtraction";

export interface UseDocumentTextCacheReturn {
  prefetchDocumentText: (body: ExtractionRequestBody) => void;
  awaitDocumentText: () => Promise<void>;
  cancelPrefetch: () => void;
}

/**
 * Manages a background prefetch of the document-text extraction endpoint.
 * The promise is stored by file ID so duplicate calls for the same document are deduped.
 */
export const useDocumentTextCache = (): UseDocumentTextCacheReturn => {
  const promiseRef = useRef<Promise<void> | null>(null);
  const fileIdRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancelPrefetch = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    promiseRef.current = null;
    fileIdRef.current = null;
  }, []);

  const prefetchDocumentText = useCallback((body: ExtractionRequestBody) => {
    if (fileIdRef.current === body.file_id && promiseRef.current) {
      return;
    }

    cancelPrefetch();

    const controller = new AbortController();
    abortRef.current = controller;
    fileIdRef.current = body.file_id;

    promiseRef.current = extractDocumentTextApi(body, controller.signal)
      .then(() => {})
      .catch((error: any) => {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
          return;
        }
        console.error("[useDocumentTextCache] Prefetch failed:", error);
      });
  }, [cancelPrefetch]);

  const awaitDocumentText = useCallback(async () => {
    if (promiseRef.current) {
      await promiseRef.current;
    }
  }, []);

  return { prefetchDocumentText, awaitDocumentText, cancelPrefetch };
};
